import {useQuery} from "@apollo/client";
import {useMemo} from "react";
import {FiLoader} from "react-icons/fi";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {shortDateTimeUser} from "@/lib/format.tsx";
import {AlertExtraDataResponse, GQL_ALERT_EXTRA_DATA} from "@/lib/queries/alerting.ts";
import {NetworkProbeResult} from "@/lib/types/network.ts";
import {DownAlert} from "@/pages/alerting/components/details/DownAlert.tsx";
import {NetworkProbePanels} from "@/pages/checks/components/runtest/network/NetworkProbePanels.tsx";

import styles from "./NetworkAlertDetails.module.scss";

/**
 * NETWORK alert details. Unlike TRANSACTION/API (which drill in from the
 * down-alerts list), a Global Ping alert opens straight into the structured
 * probe view, so this component loads the alert's ext_data itself and renders
 * the shared probe panels directly inside the Alert Details modal.
 */
export const NetworkAlertDetails = ({alertId}: {alertId: number}) => {
  const {user} = useCurrentUserContext();
  const {data, loading} = useQuery<AlertExtraDataResponse>(GQL_ALERT_EXTRA_DATA, {
    variables: {id: alertId},
    fetchPolicy: "cache-and-network",
  });

  const alertData = data?.alertData;

  // Every probe in one alert shares the alert's occurrence time; format it with the
  // user's date/time settings (same helper DownAlert uses) so the per-probe detail
  // header shows a real timestamp instead of an ISO string.
  const recordedAt =
    user && alertData?.alert?.created_at ? shortDateTimeUser(alertData.alert.created_at, user) : undefined;

  // Stabilize the reference: NetworkProbePanels resets its selected index whenever
  // `probes` changes identity, so depend on the Apollo-stable probes array.
  const probes = useMemo<NetworkProbeResult[]>(() => {
    const base = alertData?.gphttp?.probes ?? [];
    return recordedAt ? base.map((probe) => ({...probe, recorded_at: probe.recorded_at ?? recordedAt})) : base;
  }, [alertData?.gphttp?.probes, recordedAt]);

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 p-3 pulse-color-gray-600">
        <Icon icon={FiLoader} className="pulseSpin" /> Loading…
      </div>
    );
  }

  // No structured probe data on this alert — fall back to the raw alert output
  // (readable summary + traceroute text) so the modal still shows the details.
  if (probes.length === 0) {
    return alertData?.alert ? (
      <DownAlert alert={alertData.alert} />
    ) : (
      <p className="text-muted p-3 mb-0">No details were recorded for this alert.</p>
    );
  }

  // Full-bleed only around the two-pane view — it manages its own internal padding
  // and needs to reach the modal edges. The fallbacks above keep the modal padding.
  return (
    <div className={styles.fullBleed}>
      <NetworkProbePanels probes={probes} context="alert" />
    </div>
  );
};
